import React from 'react';
import { Button, Fade, Modal, Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { CSVReader } from 'react-papaparse';
import { BsDownload, BsUpload } from 'react-icons/bs';
import * as info from 'assets/data/vehicleInfo.json';
import * as XLSX from 'xlsx';
import { useDispatch } from 'react-redux';
import { setNotification } from 'redux/notifications/ducks';
import moment from 'moment';
import { bulkVehicleUpdate } from 'services/fleet_service';
import { setChangeInVehicle, vehicleUpdatedSelector } from 'redux/fleets/ducks';
import { useSelector } from 'react-redux';
import LoadAnimation from 'components/LoadAnimation';

const UpdateVehicles = ({ open, closeModalHandler, classes, t, fleet }) => {
  const dispatch = useDispatch();
  const [state, setState] = React.useState({
    status: 'idle',
    data: null,
    uploadType: '',
  });
  const vehicleDataHasChanged = useSelector(vehicleUpdatedSelector);

  const updateState = newState => {
    setState(prevState => ({ ...prevState, ...newState }));
  };
  const handleDownload = fileType => {
    if (fileType === 'xlsx') {
      const worksheet = XLSX.utils.json_to_sheet([info][0].default);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, `vehicle_data_sample`);
      XLSX.writeFile(workbook, `vehicle_data_sample.xlsx`);
    } else {
      let data = info.default;

      try {
        const worksheet = XLSX.utils.json_to_sheet(data);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        XLSX.writeFile(workbook, `vehicle_data_sample.csv`, {
          bookType: 'csv',
        });
      } catch (error) {
        console.error(error);
      }
    }
  };
  const handleOnDrop = (data, file) => {
    updateState({ data: null });
    if (file.type === 'text/csv') {
      const [headers, ...rows] = data;
      const nameColumnIndex = headers.data.indexOf('name');
      if (nameColumnIndex === -1) {
        dispatch(
          setNotification(t('csvUploaderPage.missingDataError'), 'error', true)
        );
      } else {
        updateState({
          data: data.slice(0, rows.length),
          uploadType: 'csv',
        });
      }
    } else if (
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel'
    ) {
      updateState({ uploadType: 'xslx' });
      handleOnDropExcel(file);
    }
  };

  const transformData = csvData => {
    let vehicleData = csvData;
    if (state.uploadType === 'csv') {
      const columnTitles = csvData[0].data;
      vehicleData = csvData.slice(1).map(row => {
        const tireObj = {};
        row.data.forEach((value, index) => {
          if (value || value !== '') {
            tireObj[columnTitles[index].toLowerCase()] = columnTitles[
              index
            ].includes('date')
              ? moment(value).format('YYYY-MM-DD')
              : value;
          }
        });
        return tireObj;
      });
    } else {
      vehicleData = csvData.map(vData => {
        return Object.fromEntries(
          Object.entries(vData)
            .filter(
              ([_, value]) =>
                value !== null && value !== undefined && value !== ''
            )
            .map(([key, value]) => [
              key,
              key.toLowerCase().includes('date')
                ? moment(value).format('YYYY-MM-DD')
                : value,
            ])
        );
      });
    }
    const payload = { fleet_id: fleet?.fleet_id, vehicles: vehicleData };

    return payload;
  };

  const handleOnDropExcel = async f => {
    updateState({ data: null });
    const reader = new FileReader();
    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(f);

    reader.onload = async e => {
      const bufferArray = e.target.result;

      const wb = XLSX.read(bufferArray, { type: 'buffer', cellDates: true });

      const wsname = wb.SheetNames[0];

      const ws = wb.Sheets[wsname];

      const thisData =
        XLSX.utils.sheet_to_json(ws, {
          raw: false,
          dateNF: 'yyyy-mm-dd',
        }) ?? [];
      const missingNameData = thisData.some(item => {
        return !item.name || item.name === '';
      });
      if (missingNameData) {
        dispatch(
          setNotification(t('csvUploaderPage.missingDataError'), 'error', true)
        );
      } else {
        updateState({ data: thisData, uploadType: 'xslx' });
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleOnRemoveFile = () => {
    updateState({ data: null, uploadType: '' });
  };

  const handleSubmit = async updatedData => {
    updateState({ status: 'loading' });
    const isValid = updatedData?.length;
    let payload = transformData(isValid ? updatedData : state.data);

    try {
      const res = await bulkVehicleUpdate(payload);
      if (res.status === 200) {
        dispatch(setChangeInVehicle(!vehicleDataHasChanged));
        dispatch(setNotification(t('common.success'), 'success', true));
        closeModalHandler();
      } else if (res.status === 422) {
        dispatch(setNotification(t('common.error422'), 'error', true));
      } else if(res.status === 400){
        dispatch(setNotification(res?.data?.return_message || t('common.error'), 'error', true));

      } else {
        dispatch(setNotification(t('common.error'), 'error', true));
      }
    } catch (error) {
      console.error('E21041', error);
    } finally {
      updateState({ status: 'idle' });
    }
  };

  return (
    <>
      <LoadAnimation loading={state.status === 'loading'} />
      <Modal
        aria-labelledby='transition-modal-title'
        aria-describedby='transition-modal-description'
        className={classes.modal}
        open={open}
        onClose={closeModalHandler}
        closeAfterTransition
      >
        <Fade in={open}>
          <Paper className={classes.paper}>
            <Box className={classes.box2}>
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                height='166px'
                gap={'32px'}
                bgcolor='#F3F4F6'
                width='100%'
                padding={'24px 48px 24px 48px'}
              >
                <Typography variant='h6' data-hook='csv-uploader-title'>
                  {t('csvUploaderPage.exampleFile')}
                </Typography>
                <Box display='flex' alignItems='center' gap={'16px'}>
                  <Button
                    variant='outlined'
                    startIcon={<BsDownload />}
                    color='primary'
                    className={classes.downloadBtns}
                    onClick={() => handleDownload('csv')}
                    data-hook='download-csv-btn'
                  >
                    {t('csvUploaderPage.csv')}
                  </Button>
                  <Button
                    variant='outlined'
                    startIcon={<BsDownload />}
                    color='primary'
                    className={classes.downloadBtns}
                    onClick={() => handleDownload('xlsx')}
                    data-hook='download-xlxs-btn'
                  >
                    {t('csvUploaderPage.xlsx')}
                  </Button>
                </Box>
              </Box>
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                height='100%'
                gap={'16px'}
                bgcolor='#F3F4F6'
                width='100%'
                padding={'30px 137px 30px 137px'}
              >
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  justifyContent='center'
                  height={72}
                  width={72}
                  borderRadius={16}
                  bgcolor='#FFF'
                >
                  <BsUpload size={28} />
                </Box>
                <CSVReader
                  onDrop={handleOnDrop}
                  accept={{
                    'text/csv': ['.csv'],
                    'application/vnd.ms-excel': ['.xlx'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                  }}
                  addRemoveButton
                  onRemoveFile={handleOnRemoveFile}
                >
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    gap={'24px'}
                    data-hook='upload-area'
                  >
                    <Box>
                      {t('csvUploaderPage.drag')}
                      <span className={classes.browseBtn}>
                        {t('csvUploaderPage.browse')}
                      </span>
                    </Box>
                    <span>{t('csvUploaderPage.supportedFormats')}</span>
                  </Box>
                </CSVReader>
                <Box>
                  <Typography variant='subtitle'>
                    {t('csvUploaderPage.important')}
                  </Typography>
                </Box>
                <Button
                  variant='contained'
                  color='primary'
                  data-hook='csv-upload-btn'
                  disableElevation
                  disabled={!state.data}
                  className={classes.uploadBtn}
                  onClick={handleSubmit}
                >
                  {t('fleetsSection.upload')}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </>
  );
};

export default UpdateVehicles;
